import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import QuotesFixedParameter from "../QuotesFixedParameter";
import QuotesLengthByRail from "../QuotesLengthByRail";
import QuotesBoxLengthParameter from "../QuotesBoxLengthParameter";
import QuotesVariableBoxLength from "../QuotesVariableBoxLength";
import QuotesAccessories from "../QuotesAccessories";
import {
  selectQuoteById,
  selectQuoteDetailsLoading,
} from "../../redux/selector";
import { quotesActions } from "../../redux/slice";
import { useStyles } from "./styles";

interface QuotesDetailsProps {
  quoteId: string;
}

const getLocale = (language: string) => {
  return language === "hw" || language === "he" ? "he-IL" : "en-US";
};

const formatCurrency = (value: number) => {
  return `$${value.toLocaleString()}`;
};

const formatDate = (value: string, language: string) => {
  return new Intl.DateTimeFormat(getLocale(language), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(value));
};

const summaryItems = [
  { labelKey: "QUOTES.DETAILS.SUMMARY.TOTAL_PRICE", value: "₪ 16,061" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.TOTAL_LABOR", value: "₪ 23,383" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.MATERIAL_PRICE", value: "₪ 31,518" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.VARIABLE_ADVANCE", value: "₪ 10,716" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.SALE_PRICE", value: "₪ 44,235" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.ADDITIONAL_PRICE", value: "₪ 0" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.STANDARD_HOURS", value: "81.4" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.REPAIR_HOURS", value: "0" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.WORK_HOURS", value: "81.4" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.CUT_COST", value: "₪ 7,322" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.WORK_COST_90", value: "₪ 7,322" },
  { labelKey: "QUOTES.DETAILS.SUMMARY.RADIATOR_WEIGHT", value: "2924 קג" },
];

const liftingOptions = [
  "QUOTES.DETAILS.LIFTING.NONE",
  "QUOTES.DETAILS.LIFTING.HEAVY",
  "QUOTES.DETAILS.LIFTING.STANDARD",
];

const equipmentOptions = [
  "QUOTES.DETAILS.EQUIPMENT.SIDE_AND_AIR",
  "QUOTES.DETAILS.EQUIPMENT.LONG_PROTECTION_FRAME",
  "QUOTES.DETAILS.EQUIPMENT.HINGED_DOOR",
  "QUOTES.DETAILS.EQUIPMENT.THREE_HINGES",
  "QUOTES.DETAILS.EQUIPMENT.SIDE_PROTECTOR",
  "QUOTES.DETAILS.EQUIPMENT.REAR_LIGHT_PROTECTION",
  "QUOTES.DETAILS.EQUIPMENT.SIDE_LIGHTS",
  "QUOTES.DETAILS.EQUIPMENT.REAR_LIGHT_COVER",
  "QUOTES.DETAILS.EQUIPMENT.SPARE_WHEEL_HOLDER",
  "QUOTES.DETAILS.EQUIPMENT.FRONT_TEST_FRAME",
  "QUOTES.DETAILS.EQUIPMENT.FRONT_PANEL",
  "QUOTES.DETAILS.EQUIPMENT.TOOLBOX",
  "QUOTES.DETAILS.EQUIPMENT.FOLDING_LADDER",
  "QUOTES.DETAILS.EQUIPMENT.INTERNAL_CLOSING",
  "QUOTES.DETAILS.EQUIPMENT.PLASTIC_COVER",
  "QUOTES.DETAILS.EQUIPMENT.OTHER_ACCESSORY",
];

const quantityItems = [
  { labelKey: "QUOTES.DETAILS.QUANTITIES.WIDE_WINCH", value: 0 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.SIDE_WINCH", value: 1 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.FRONT_WINCH", value: 4 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.DOOR", value: 2 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.PILLAR", value: 3 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.FLOOR_RING", value: 4 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.TIE_RING", value: 0 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.EXTENSION_RAILS", value: 4 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.BLINKER_50", value: 3 },
  { labelKey: "QUOTES.DETAILS.QUANTITIES.BLINKER_60", value: 4 },
];

const QuotesDetails = ({ quoteId }: QuotesDetailsProps) => {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const quote = useSelector((state) => selectQuoteById(state as any, quoteId));
  const loading = useSelector(selectQuoteDetailsLoading);

  useEffect(() => {
    dispatch(quotesActions.getQuoteDetails(quoteId));
  }, [dispatch, quoteId]);

  if (loading && !quote) {
    return (
      <Box className={styles.root}>
        <Box className={styles.loadingWrapper}>
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  if (!quote) {
    return (
      <Box className={styles.root}>
        <Typography className={styles.emptyText}>
          {t("QUOTES.DETAILS.NOT_FOUND")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.root}>
      <Box className={styles.card}>
        <Typography className={styles.sectionHeading}>
          {t("QUOTES.DETAILS.VALUE_SUMMARY")}
        </Typography>

        <Box className={styles.summaryGrid}>
          {summaryItems.map((item) => (
            <Box className={styles.summaryCard} key={item.labelKey}>
              <Typography className={styles.summaryLabel}>
                {t(item.labelKey)}
              </Typography>

              <Typography className={styles.summaryValue}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box className={styles.divider} />

        <Box className={styles.detailsGrid}>
          <Box className={styles.infoColumn}>
            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.TABLE.QUOTE_NUMBER")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {quote.quoteNumber}
              </Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.TABLE.CUSTOMER_NAME")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {t(quote.customerNameKey)}
              </Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.TABLE.PROJECT_TYPE")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {t(quote.projectTypeKey)}
              </Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.DETAILS.WEIGHT")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {t("QUOTES.DETAILS.STATIC.WEIGHT")}
              </Typography>
            </Box>
          </Box>

          <Box className={styles.infoColumn}>
            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.TABLE.TOTAL_VALUE")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {formatCurrency(quote.totalValue)}
              </Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.TABLE.CREATED_AT")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {formatDate(quote.createdAt, i18n.language)}
              </Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.TABLE.VALID_UNTIL")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {formatDate(quote.validUntil, i18n.language)}
              </Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.DETAILS.BASE")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {t("QUOTES.DETAILS.STATIC.BASE")}
              </Typography>
            </Box>
          </Box>

          <Box className={styles.infoColumn}>
            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.DETAILS.BODY_LENGTH")}:
              </Typography>
              <Typography className={styles.infoValue}>8.60 m</Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.DETAILS.BODY_WIDTH")}:
              </Typography>
              <Typography className={styles.infoValue}>2.50 m</Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.DETAILS.FRONT_WALL")}:
              </Typography>
              <Typography className={styles.infoValue}>1.80 m</Typography>
            </Box>

            <Box className={styles.infoRow}>
              <Typography className={styles.infoLabel}>
                {t("QUOTES.DETAILS.CRANE")}:
              </Typography>
              <Typography className={styles.infoValue}>
                {t("QUOTES.DETAILS.NONE")}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box className={styles.divider} />

        <Box className={styles.radioRow}>
          <Typography className={styles.sectionTitle}>
            {t("QUOTES.DETAILS.LIFTING_SYSTEM")}
          </Typography>

          <Box className={styles.radioGroup}>
            {liftingOptions.map((labelKey, index) => (
              <FormControlLabel
                key={labelKey}
                className={styles.optionLabel}
                control={<Radio checked={index === 0} size="small" />}
                label={t(labelKey)}
              />
            ))}
          </Box>
        </Box>

        <Typography className={styles.subSectionTitle}>
          {t("QUOTES.DETAILS.STANDARD_1995")}
        </Typography>

        <Box className={styles.checkGrid}>
          {equipmentOptions.map((labelKey, index) => (
            <Box className={styles.checkItem} key={labelKey}>
              <FormControlLabel
                className={styles.checkLabel}
                control={<Checkbox checked={index < 12} size="small" />}
                label={t(labelKey)}
              />
            </Box>
          ))}
        </Box>

        <Box className={styles.divider} />

        <Box className={styles.quantityGrid}>
          {quantityItems.map((item) => (
            <Box className={styles.quantityItem} key={item.labelKey}>
              <Typography className={styles.quantityLabel}>
                {t(item.labelKey)}
              </Typography>

              <Box className={styles.quantityBox}>
                <Box className={styles.quantityArrows}>
                  <KeyboardArrowUpIcon className={styles.quantityArrowIcon} />
                  <KeyboardArrowDownIcon className={styles.quantityArrowIcon} />
                </Box>

                <Typography className={styles.quantityValue}>
                  {item.value}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      <QuotesFixedParameter />
      <QuotesLengthByRail />
      <QuotesBoxLengthParameter />
      <QuotesVariableBoxLength />
      <QuotesAccessories />
    </Box>
  );
};

export default QuotesDetails;